import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";
import { StepBar } from "../components/StepBar";
import { DesignCard } from "../components/DesignCard";
import { CATS } from "../data/constants";
import { useAuth } from "../lib/auth.jsx";
import { bookingDisplayCode } from "../lib/bookingCode.js";

const CURRENCY_SYMBOLS = {
  USD: "$", CAD: "CA$", GBP: "£", EUR: "€", NGN: "₦",
  GHS: "GH₵", ZAR: "R", KES: "KSh", AUD: "A$", NZD: "NZ$",
  AED: "د.إ", INR: "₹", SGD: "S$", MYR: "RM", JPY: "¥",
  BRL: "R$", MXN: "MX$", EGP: "E£", SAR: "﷼", ILS: "₪",
  CNY: "¥", KRW: "₩", THB: "฿", HKD: "HK$", IDR: "Rp",
};

function fmtCurrency(cents, currencyCode = "USD") {
  const code = String(currencyCode || "USD").toUpperCase();
  const amount = (Number(cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${CURRENCY_SYMBOLS[code] || code}${amount}`;
}

function localDateValue(date = new Date()) {
  const copy = new Date(date);
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset());
  return copy.toISOString().slice(0, 10);
}

function toLocalRequestIso(dateValue, timeValue) {
  const date = new Date(`${dateValue}T${timeValue || "10:00"}`);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toISOString();
}

export function BookingFlow() {
  const { profile, authedFetch } = useAuth();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const reuseId = search.get("reuse");
  const [quota, setQuota] = useState(null);
  const [step, setStep] = useState(1);
  const [cat, setCat] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [picked, setPicked] = useState(null);
  const [pickedIndex, setPickedIndex] = useState(null);
  const [final, setFinal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [plans, setPlans] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(null);

  // Booking step 4 → choose slot
  const [slots, setSlots] = useState([]);
  const [nextEstimate, setNextEstimate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDone, setBookingDone] = useState(null);
  const [tentativeDone, setTentativeDone] = useState(null);
  const [tentativeDate, setTentativeDate] = useState(() => localDateValue(new Date(Date.now() + 86400_000)));
  const [tentativeTime, setTentativeTime] = useState("10:00");
  const [tentativeNote, setTentativeNote] = useState("");
  const [bookingSettings, setBookingSettings] = useState({
    depositEnabled: false,
    depositPercent: 30,
    currencyCode: "USD",
    defaultBookingPriceCents: 0,
  });

  // Tip + promo (step 5)
  const [tipPct, setTipPct] = useState(15);
  const [customTipDollars, setCustomTipDollars] = useState("");
  const [promo, setPromo] = useState("");
  const [promoOk, setPromoOk] = useState(null);   // { code, discountCents } or null
  const [promoErr, setPromoErr] = useState("");
  const [paymentChoice, setPaymentChoice] = useState(null);
  const [deferredInvoice, setDeferredInvoice] = useState(null);

  // Stripe is the default everywhere. OPay is only used when the customer
  // explicitly selects it in their profile.
  const [paymentGateway, setPaymentGateway] = useState("stripe");

  function imageCountForProfile(profile) {
    const plan = (profile?.plan || "free").toLowerCase();
    const tier = (profile?.membership_tier || "free").toLowerCase();
    if (plan === "unlimited" || tier === "gold") return 6;
    if (plan === "studio"    || tier === "silver") return 3;
    return 1; // free
  }

  const imageCount = imageCountForProfile(profile);
  const generateLabel = imageCount === 1 ? "Generate Image" : "Generate Images";

  // Pull the current design quota for the banner. We swallow errors — the
  // server already enforces the limit, this is just to inform the user.
  useEffect(() => {
    let alive = true;
    authedFetch("/api/designs/quota").then((q) => { if (alive) setQuota(q?.quota || null); }).catch(() => {});
    authedFetch("/api/settings/booking").then((s) => {
      if (alive && s) setBookingSettings((prev) => ({
        ...prev,
        depositEnabled: !!s.depositEnabled,
        depositPercent: Number(s.depositPercent || 30),
        defaultBookingPriceCents: Number(s.defaultBookingPriceCents || prev.defaultBookingPriceCents || 0),
        currencyCode: String(s.currency || prev.currencyCode || "USD").toUpperCase(),
      }));
    }).catch(() => {});
    authedFetch("/api/settings/currency").then((s) => {
      if (!alive) return;
      if (s?.currency) setBookingSettings((prev) => ({ ...prev, currencyCode: String(s.currency).toUpperCase() }));
    }).catch(() => {});
    setPaymentGateway(profile?.preferred_payment_gateway === "opay" ? "opay" : "stripe");
    authedFetch("/api/subscriptions/plans").then((p) => {
      if (alive && p?.plans) setPlans(p.plans);
    }).catch(() => {});
    return () => { alive = false; };
  }, [profile?.preferred_payment_gateway]);

  // If we landed here via "Use this design" from the history modal, jump
  // straight to the slot picker with the previously-finalised design.
  useEffect(() => {
    if (!reuseId) return;
    let payload = null;
    try { payload = JSON.parse(sessionStorage.getItem("oge.reuse") || "null"); } catch {}
    if (!payload || payload.requestId !== reuseId) return;
    setRequestId(payload.requestId);
    setFinal(payload.design);
    setPicked(payload.design);
    setStep(4);
    // Load slots — narrow to the assigned tech if the request has one.
    authedFetch(`/api/availability?requestId=${encodeURIComponent(payload.requestId)}`)
      .then((av) => { setSlots(av.slots); setNextEstimate(av.nextEstimate); })
      .catch((e) => setErr(e.message));
    sessionStorage.removeItem("oge.reuse");
  }, [reuseId]);

  async function generate() {
    setLoading(true); setErr("");
    try {
      const out = await authedFetch("/api/designs/generate", {
        method: "POST",
        body: JSON.stringify({ category: cat }),
      });
      setRequestId(out.requestId);
      setDesigns(out.designs);
      if (out.quota) setQuota(out.quota);
      setStep(3);
    } catch (e) {
      setErr(e.message);
      // Bounce them back to step 1 so they can re-pick or re-use a past design.
      setStep(1);
      // Refresh quota to show 0 remaining.
      authedFetch("/api/designs/quota").then((q) => setQuota(q?.quota || null)).catch(() => {});
    }
    setLoading(false);
  }

  async function startMembership(tier) {
    setMembershipLoading(tier);
    setErr("");
    try {
      const endpoint = paymentGateway === "opay"
        ? "/api/payments/opay/subscription"
        : "/api/subscriptions/checkout";
      const out = await authedFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({
          tier,
          currency: paymentGateway === "opay" ? undefined : bookingSettings.currencyCode,
        }),
      });
      if (out?.url) {
        window.location.href = out.url;
      } else {
        setErr("Checkout is not available for this plan right now.");
      }
    } catch (e) {
      setErr(e.message || "Could not start checkout.");
    } finally {
      setMembershipLoading(null);
    }
  }

  async function generateFromImage(file) {
    setLoading(true); setErr("");
    setStep(2);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const out = await authedFetch("/api/designs/from-image", {
        method: "POST",
        body: JSON.stringify({ imageDataUrl: dataUrl, category: cat || "fashion" }),
      });
      setRequestId(out.requestId);
      setDesigns(out.designs);
      setStep(3);
    } catch (e) { setErr(e.message); setStep(1); }
    setLoading(false);
  }

  async function finalise(d) {
    setLoading(true); setErr("");
    try {
      const idx = designs.findIndex((x) => x.id === d.id);
      await authedFetch(`/api/designs/${requestId}/finalise`, {
        method: "POST",
        body: JSON.stringify({ designIndex: idx }),
      });
      setFinal(d); setPickedIndex(idx);
      setTentativeDone(null);
      // Load slots. Pass the requestId so the server can narrow to the
      // admin-assigned tech's calendar when one's been set — that way the
      // booking lands on the correct tech's dashboard.
      const av = await authedFetch(`/api/availability?requestId=${encodeURIComponent(requestId)}`);
      setSlots(av.slots); setNextEstimate(av.nextEstimate);
      setStep(4);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }

  async function createBooking(slot = selectedSlot) {
    if (loading || bookingDone) return null;
    if (!slot) return null;
    setLoading(true); setErr("");
    try {
      const out = await authedFetch("/api/bookings", {
        method: "POST",
        body: JSON.stringify({
          requestId,
          slotId: slot.id,
          designId: final?.id || picked?.id,
          designIndex: Number.isInteger(pickedIndex) ? pickedIndex : undefined,
        }),
      });
      setBookingDone(out.booking);
      return out.booking;
    } catch (e) { setErr(e.message); return null; }
    finally { setLoading(false); }
  }

  async function createTentativeBooking() {
    if (loading || tentativeDone) return;
    const requestedStartAt = toLocalRequestIso(tentativeDate, tentativeTime);
    if (!requestedStartAt) {
      setErr("Choose a tentative date and time.");
      return;
    }
    setLoading(true); setErr("");
    try {
      const out = await authedFetch("/api/bookings/tentative", {
        method: "POST",
        body: JSON.stringify({
          requestId,
          requestedStartAt,
          requestedTimeNote: tentativeNote.trim() || undefined,
          designId: final?.id || picked?.id,
          designIndex: Number.isInteger(pickedIndex) ? pickedIndex : undefined,
        }),
      });
      setBookingDone(out.booking);
      setTentativeDone(out.booking);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function book(slot) {
    const booking = await createBooking(slot);
    if (booking) {
      setStep(5);
    }
  }

  function tipCents() {
    if (!bookingDone) return 0;
    if (customTipDollars.trim() !== "") {
      const n = Number(customTipDollars);
      return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : 0;
    }
    return Math.round(bookingDone.price_cents * (tipPct / 100));
  }

  async function applyPromo() {
    setPromoErr(""); setPromoOk(null);
    try {
      const out = await authedFetch("/api/promotions/validate", {
        method: "POST",
        body: JSON.stringify({ code: promo, baseAmountCents: bookingDone.price_cents }),
      });
      setPromoOk({ code: out.promotion.code, discountCents: out.discountCents });
    } catch (e) { setPromoErr(e.message); }
  }

  async function declarePaid() {
    if (!deferredInvoice) return;
    setLoading(true); setErr("");
    try {
      const out = await authedFetch(`/api/invoices/${deferredInvoice.id}/declare-paid`, { method: "POST" });
      setDeferredInvoice(out.invoice);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }

  async function createDeferredPayment(method, targetBooking = bookingDone) {
    if (!targetBooking) return;
    setLoading(true); setErr("");
    try {
      const out = await authedFetch("/api/payments/manual", {
        method: "POST",
        body: JSON.stringify({
          bookingId: targetBooking.id,
          method,
          tipCents: tipCents(),
          promoCode: promoOk?.code || undefined,
        }),
      });
      setPaymentChoice(method);
      setDeferredInvoice(out.invoice);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }

  async function cancelPaymentHold(booking) {
    if (!booking?.id) return;
    try {
      await authedFetch(`/api/bookings/${booking.id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason: "Deposit payment was not completed." }),
      });
    } catch {}
  }

  async function startDepositPayment(method, targetBooking) {
    if (!targetBooking) return;
    setLoading(true); setErr("");
    try {
      const endpoint = paymentGateway === "opay"
        ? "/api/payments/opay/booking"
        : "/api/payments/checkout";
      const out = await authedFetch(endpoint, {
        method: "POST",
        body: JSON.stringify(paymentGateway === "opay"
          ? {
              bookingId: targetBooking.id,
              tipCents: 0,
              promoCode: promoOk?.code || undefined,
            }
          : {
              bookingId: targetBooking.id,
              mode: "deposit",
              method,
              tipCents: 0,
              promoCode: promoOk?.code || undefined,
            }),
      });
      if (!out?.url) throw new Error("Payment checkout is not available right now.");
      window.location.href = out.url;
    } catch (e) {
      await cancelPaymentHold(targetBooking);
      setBookingDone(null);
      setErr(e.message || "Could not start deposit payment.");
    } finally {
      setLoading(false);
    }
  }

  async function confirmDeferred(method) {
    const booking = await createBooking();
    if (!booking) return;
    setBookingDone(booking);
    setPaymentChoice(method);
    if (bookingSettings.depositEnabled) {
      await startDepositPayment(method, booking);
    } else {
      setStep(5);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: step === 3 ? 1180 : 720, margin: "0 auto", padding: "30px 16px", transition: "max-width .2s ease" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
          <StepBar current={step === 5 && deferredInvoice ? 6 : step} />
        </div>

        {step === 1 && (
          <Card>
            <SectionHeader emoji="🎀" title="What's the Occasion?"
              subtitle="Choose your vibe — AI tailors every design to match" />
            <QuotaBanner
              quota={quota}
              plans={plans}
              currencyCode={bookingSettings.currencyCode}
              loadingTier={membershipLoading}
              onSubscribe={startMembership}
            />
            <div
              role="radiogroup"
              aria-label="Occasion"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: 12,
                alignItems: "stretch",
              }}>
              {CATS.map((c) => {
                const sel = cat === c.id;
                return (
                  <button key={c.id} type="button" role="radio" aria-checked={sel}
                    onClick={() => setCat(c.id)}
                    onMouseEnter={(e) => {
                      if (!sel) e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                    style={{
                      background: sel
                        ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))"
                        : "var(--bg-chip)",
                      border: `1.5px solid ${sel ? "var(--mauve-deep)" : "var(--border)"}`,
                      borderRadius: 18,
                      padding: "16px 14px",
                      minHeight: 142,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      textAlign: "left", color: sel ? "#fff" : "var(--text)",
                      boxShadow: sel
                        ? "0 16px 32px rgba(196, 135, 154, 0.28)"
                        : "var(--shadow-sm)",
                      backdropFilter: sel ? "none" : "blur(14px)",
                      transition: "transform .2s var(--ease-out), box-shadow .2s var(--ease-out), border-color .2s var(--ease-out), background .2s var(--ease-out)",
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                      <div
                        aria-hidden="true"
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 14,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 20,
                          background: sel ? "rgba(255,255,255,0.14)" : "var(--bg-soft)",
                          border: sel ? "1px solid rgba(255,255,255,0.12)" : "1px solid var(--border)",
                          flexShrink: 0,
                        }}
                      >
                        {c.icon}
                      </div>
                      <div
                        aria-hidden="true"
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 999,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: sel ? "1px solid rgba(255,255,255,0.28)" : "1px solid var(--border)",
                          background: sel ? "rgba(255,255,255,0.12)" : "transparent",
                          color: sel ? "#fff" : "var(--text-muted)",
                          fontSize: 12,
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                      >
                        {sel ? "✓" : "•"}
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>
                        {c.label}
                      </div>
                      <div style={{ fontSize: 11.5, marginTop: 6, color: sel ? "rgba(255,255,255,0.78)" : "var(--text-muted)", lineHeight: 1.45 }}>
                        {c.desc}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
              <label style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, color: "var(--accent)", fontSize: 13, fontWeight: 600 }}>
                <input type="file" accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) generateFromImage(f); }} />
                🖼 Use an inspo photo instead
              </label>
              <Button label={generateLabel} icon="✨" variant="gold" size="lg"
                disabled={!cat || loading} loading={loading} onClick={() => { setStep(2); generate(); }} />
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <SectionHeader emoji="🎨" title="Crafting your designs…"
              subtitle={`Our AI is writing ${imageCount} unique look${imageCount === 1 ? '' : 's'} and rendering ${imageCount === 1 ? 'it' : 'each'} on a real hand. ~30–60 seconds.`} />
            <div style={{ display: "flex", justifyContent: "center", padding: "30px 0" }}>
              <div style={{ display: "flex", gap: 10 }}>
                {[0,1,2,3,4].map(i => (
                  <div key={i} style={{ width: 12, height: 12, borderRadius: "50%", background: "var(--gold)",
                    animation: `pulse 1.4s ${i*0.22}s ease-in-out infinite` }} />
                ))}
              </div>
            </div>
            {err && <ErrorBox msg={err} onRetry={() => generate()} onDismiss={() => setErr("")} />}
            {quota?.remaining <= 0 && (
              <SubscriptionUpgradeOptions
                plans={plans}
                currencyCode={bookingSettings.currencyCode}
                loadingTier={membershipLoading}
                onSubscribe={startMembership}
              />
            )}
          </Card>
        )}

        {step === 3 && (
          <div>
            <header className="fadeUp" style={{ background: "linear-gradient(135deg, var(--plum), var(--noir))",
              borderRadius: 22, padding: "20px 22px", marginBottom: 20, color: "#fff" }}>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700 }}>
                Your designs, {profile?.full_name?.split(" ")[0] || "friend"}
              </div>
              <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
                Tap a card you love, then "Continue to Booking".
              </div>
              {/* Each design card shows the look painted on a photoreal hand
                  generated to match the customer's profile. The reminder
                  here makes it obvious the preview IS personalised, and
                  nudges them to set skin tone if missing for an even more
                  accurate render. */}
              <SkinTonePreviewBanner skinTone={profile?.skin_tone} />
            </header>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 340px))", gap: 16, justifyContent: "center" }}>
              {designs.map((d, i) => (
                <DesignCard key={d.id} design={d} index={i}
                  requestId={requestId} designIndex={i}
                  selected={picked?.id === d.id}
                  onSelect={setPicked}
                  onFinalise={finalise} />
              ))}
            </div>
          </div>
        )}

        {step === 4 && tentativeDone && (
          <Card>
            <SectionHeader
              emoji="🗓️"
              title="Tentative request sent"
              subtitle="The studio received your preferred time. You can keep chatting while they confirm a real slot."
            />
            <div style={{ background: "var(--bg-soft)", border: "1px solid var(--border)", borderRadius: 16, padding: 16, textAlign: "left" }}>
              <div style={{ fontFamily: "var(--font-display)", color: "var(--text)", fontSize: 18, fontWeight: 800 }}>
                {final?.name || "Selected design"}
              </div>
              <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 6 }}>
                Requested for {new Date(tentativeDone.requested_start_at || tentativeDone.created_at).toLocaleString()}.
                {tentativeDone.technician?.full_name ? ` Assigned to ${tentativeDone.technician.full_name}.` : ""}
              </div>
              {tentativeDone.requested_time_note && (
                <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 8 }}>
                  Note: {tentativeDone.requested_time_note}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 18 }}>
              <Button label="View My Bookings" variant="primary" onClick={() => nav("/history")} />
              <Button label="Open Chat" variant="outline" onClick={() => nav("/chat")} />
            </div>
            {err && <div style={{ marginTop: 14 }}><ErrorBox msg={err} onDismiss={() => setErr("")} /></div>}
          </Card>
        )}

        {step === 4 && !tentativeDone && (
          <Card>
            <SectionHeader emoji="📅" title="Pick a time"
              subtitle={`Your design "${final?.name}" is locked in. Pick an open slot below.`} />
            {slots.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
                <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)" }}>No openings in the next 14 days</h3>
                {nextEstimate ? (
                  <p style={{ color: "var(--text-muted)", marginTop: 8 }}>
                    Estimated next available: <b style={{ color: "var(--text-accent)" }}>
                      {new Date(nextEstimate.starts_at).toLocaleString()}
                    </b>
                  </p>
                ) : (
	                  <p style={{ color: "var(--text-muted)", marginTop: 8 }}>
	                    Our admin technician hasn't published availability yet. Please check back soon.
	                  </p>
	                )}
                <TentativeTimeForm
                  date={tentativeDate}
                  time={tentativeTime}
                  note={tentativeNote}
                  loading={loading}
                  onDate={setTentativeDate}
                  onTime={setTentativeTime}
                  onNote={setTentativeNote}
                  onSubmit={createTentativeBooking}
                />
	              </div>
	            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                {slots.map((s) => (
                  <button key={s.id} onClick={() => setSelectedSlot(s)} disabled={loading}
                    style={{
                      background: selectedSlot?.id === s.id ? "rgba(196,135,154,0.16)" : "var(--bg-elevated)",
                      border: `1.5px solid ${selectedSlot?.id === s.id ? "var(--accent)" : "var(--border)"}`,
                      borderRadius: 14, padding: "14px 16px", cursor: "pointer", textAlign: "left",
                      transition: "all .2s",
                    }}>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 600 }}>
                      {new Date(s.starts_at).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)", marginTop: 2 }}>
                      {new Date(s.starts_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {selectedSlot && (
              <BookingSummary
                slot={selectedSlot}
                design={final}
                settings={bookingSettings}
                loading={loading}
                onConfirmCash={() => confirmDeferred("cash")}
                onConfirmTransfer={() => confirmDeferred("transfer")}
              />
            )}
            {err && <div style={{ marginTop: 14 }}><ErrorBox msg={err} onDismiss={() => setErr("")} /></div>}
          </Card>
        )}

        {step === 5 && bookingDone && (
          <Card>
            <SectionHeader emoji="🎉" title="Booked!"
              subtitle="Your appointment is reserved. Complete your payment details below." />
            <div style={{ background: "var(--bg-soft)", borderRadius: 14, padding: 16, marginBottom: 18, textAlign: "left" }}>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18, color: "var(--text-on-soft)" }}>{final?.name}</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
                Booking #{bookingDisplayCode(bookingDone.id)} · base {fmtCurrency(bookingDone.price_cents, bookingSettings.currencyCode)}
              </div>

              {/* Tip selector */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                  Add a tip for your technician
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {[0, 15, 18, 20, 25].map(p => (
                    <button key={p} type="button" onClick={() => { setTipPct(p); setCustomTipDollars(""); }}
                      style={{
                        padding: "6px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer",
                        background: tipPct === p && customTipDollars === "" ? "var(--accent)" : "var(--bg-elevated)",
                        color:      tipPct === p && customTipDollars === "" ? "#fff" : "var(--text)",
                        border: "1.5px solid var(--border-strong)",
                      }}>{p === 0 ? "No tip" : p + "%"}</button>
                  ))}
                  <input type="number" min="0" placeholder={`Custom ${CURRENCY_SYMBOLS[bookingSettings.currencyCode] || bookingSettings.currencyCode}`}
                    value={customTipDollars} onChange={(e) => setCustomTipDollars(e.target.value)}
                    style={{
                      width: 90, padding: "6px 10px", borderRadius: 999, fontSize: 13,
                      border: "1.5px solid var(--border-strong)", background: "var(--bg-elevated)", color: "var(--text)",
                    }} />
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                  Tip: {fmtCurrency(tipCents(), bookingSettings.currencyCode)}
                </div>
              </div>

              {/* Promo code */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                  Promo code
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())}
                    placeholder="WELCOME10"
                    style={{
                      flex: 1, padding: "8px 12px", borderRadius: 10, fontSize: 13,
                      border: "1.5px solid var(--border-strong)", background: "var(--bg-elevated)", color: "var(--text)",
                      letterSpacing: 1,
                    }} />
                  <Button label="Apply" variant="outline" size="sm" onClick={applyPromo} disabled={!promo.trim()} />
                </div>
                {promoOk && (
                  <div style={{ fontSize: 12, color: "var(--text-confirm)", marginTop: 6 }}>
                    ✓ {promoOk.code} applied — saves {fmtCurrency(promoOk.discountCents, bookingSettings.currencyCode)}
                  </div>
                )}
                {promoErr && (
                  <div role="alert" style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 6 }}>{promoErr}</div>
                )}
              </div>

              <div style={{ borderTop: "1px solid var(--border)", marginTop: 14, paddingTop: 12, fontWeight: 700, color: "var(--text)", fontSize: 14, display: "flex", justifyContent: "space-between" }}>
                <span>Total today</span>
                <span>
                  {fmtCurrency(bookingDone.price_cents + tipCents() - (promoOk?.discountCents || 0), bookingSettings.currencyCode)}
                </span>
              </div>
            </div>
            {/* Step 1 of payment: create the invoice by confirming method */}
            {!deferredInvoice && (
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <Button label="I'll Pay Cash" icon="💵"
                  variant={paymentChoice === "cash" ? "gold" : "outline"} size="lg"
                  onClick={() => createDeferredPayment("cash")}
                  loading={loading && paymentChoice === "cash"} />
                <Button label="I'll Pay via Transfer" icon="🏦"
                  variant={paymentChoice === "transfer" ? "gold" : "outline"} size="lg"
                  onClick={() => createDeferredPayment("transfer")}
                  loading={loading && paymentChoice === "transfer"} />
                <Button label="View My Bookings" variant="outline" onClick={() => nav("/history")} />
              </div>
            )}

            {/* Step 2: invoice created — customer declares they've paid */}
            {deferredInvoice && !deferredInvoice.customer_declared_at && (
              <div>
                <div style={{
                  padding: 14, borderRadius: 14, background: "var(--bg-soft)",
                  border: "1px solid var(--border-strong)", color: "var(--text)", textAlign: "left", marginBottom: 14,
                }}>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>
                    {paymentChoice === "cash" ? "Cash" : "Bank Transfer"} — Invoice {deferredInvoice.number || deferredInvoice.id?.slice(0, 8)}
                  </div>
                  {bookingSettings.depositEnabled && (
                    <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
                      Deposit ({bookingSettings.depositPercent}%) due before your appointment.
                      Remaining balance due after service.
                    </div>
                  )}
                  <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
                    Once you've sent payment, tap the button below so the admin can confirm it.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <Button
                    label={`I've Paid via ${paymentChoice === "cash" ? "Cash" : "Transfer"}`}
                    icon="✅" variant="gold" size="lg"
                    onClick={declarePaid} loading={loading} />
                  <Button label="View My Bookings" variant="outline" onClick={() => nav("/history")} />
                </div>
              </div>
            )}

            {/* Step 3: declared — awaiting admin confirmation */}
            {deferredInvoice?.customer_declared_at && (
              <div style={{
                padding: 16, borderRadius: 14, marginBottom: 8, textAlign: "center",
                background: "rgba(196,135,154,0.12)", border: "1.5px solid rgba(196,135,154,0.4)",
              }}>
                <div style={{ fontSize: 22, marginBottom: 8 }}>⏳</div>
                <div style={{ fontWeight: 800, color: "var(--text)", marginBottom: 4 }}>
                  Payment declared — awaiting admin confirmation
                </div>
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                  Invoice {deferredInvoice.number} · {paymentChoice === "cash" ? "Cash" : "Transfer"}.
                  You'll receive a confirmation once the admin verifies your payment.
                </div>
                <div style={{ marginTop: 12 }}>
                  <Button label="View My Bookings" variant="outline" onClick={() => nav("/history")} />
                </div>
              </div>
            )}
            {err && <div style={{ marginTop: 14 }}><ErrorBox msg={err} onDismiss={() => setErr("")} /></div>}
          </Card>
        )}
      </div>
    </div>
  );
}

function ErrorBox({ msg, onRetry, onDismiss }) {
  return (
    <div role="alert" style={{
      background: "var(--bg-soft)", border: "1.5px solid var(--border-strong)", borderRadius: 14, padding: "14px 16px",
    }}>
      <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 700 }}>{msg}</div>
      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
        {onDismiss && <Button label="Dismiss" variant="danger" size="sm" onClick={onDismiss} />}
        {onRetry && <Button label="Try Again" variant="primary" size="sm" onClick={onRetry} />}
      </div>
    </div>
  );
}

function BookingSummary({ slot, design, settings, loading, onConfirmCash, onConfirmTransfer }) {
  const currencyCode = settings.currencyCode || "USD";
  const totalCents = Number(design?.price_cents || settings.defaultBookingPriceCents || 0);
  const depositCents = settings.depositEnabled
    ? Math.round(totalCents * Number(settings.depositPercent || 30) / 100)
    : 0;
  const balanceCents = Math.max(0, totalCents - depositCents);
  return (
    <div style={{
      marginTop: 18,
      padding: 18,
      borderRadius: 18,
      background: "var(--bg-soft)",
      border: "1.5px solid var(--border-strong)",
      color: "var(--text)",
      textAlign: "left",
      boxShadow: "var(--shadow-sm)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, color: "var(--accent)", textTransform: "uppercase" }}>
            Booking summary
          </div>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800, color: "var(--text)", marginTop: 4 }}>
            {design?.name || "Selected design"}
          </div>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 5 }}>
            {new Date(slot.starts_at).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at{" "}
            {new Date(slot.starts_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
          </div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: "var(--text)" }}>
          {fmtCurrency(totalCents, currencyCode)}
        </div>
      </div>

      {settings.depositEnabled && (
        <div style={{
          marginTop: 14,
          padding: 13,
          borderRadius: 14,
          background: "rgba(196,135,154,0.12)",
          border: "1px solid rgba(196,135,154,0.35)",
          color: "var(--text)",
          lineHeight: 1.45,
          fontSize: 13,
        }}>
          A {settings.depositPercent}% deposit ({fmtCurrency(depositCents, currencyCode)}) is required before your appointment.
          The remaining {fmtCurrency(balanceCents, currencyCode)} is due after service.
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
        <Button
          label={settings.depositEnabled ? `Book — Pay Cash` : "Confirm Cash"}
          icon="💵"
          variant="primary"
          size="lg"
          loading={loading}
          onClick={onConfirmCash}
        />
        <Button
          label={settings.depositEnabled ? `Book — Pay via Transfer` : "Confirm Transfer"}
          icon="🏦"
          variant="outline"
          size="lg"
          loading={loading}
          onClick={onConfirmTransfer}
        />
      </div>
    </div>
  );
}

function TentativeTimeForm({ date, time, note, loading, onDate, onTime, onNote, onSubmit }) {
  const minDate = localDateValue(new Date());
  return (
    <div style={{
      margin: "24px auto 0",
      maxWidth: 560,
      padding: 16,
      borderRadius: 18,
      background: "var(--bg-soft)",
      border: "1.5px solid var(--border-strong)",
      textAlign: "left",
    }}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 800, color: "var(--text)" }}>
        Request a tentative time
      </div>
      <p style={{ color: "var(--text-muted)", fontSize: 13, lineHeight: 1.5, margin: "6px 0 14px" }}>
        Pick the time that works for you. The studio will confirm or suggest the closest available slot.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 170px), 1fr))", gap: 10 }}>
        <label style={tentativeLabelStyle()}>
          Date
          <input
            type="date"
            min={minDate}
            value={date}
            onChange={(event) => onDate(event.target.value)}
            style={tentativeInputStyle()}
          />
        </label>
        <label style={tentativeLabelStyle()}>
          Time
          <input
            type="time"
            value={time}
            onChange={(event) => onTime(event.target.value)}
            style={tentativeInputStyle()}
          />
        </label>
      </div>
      <label style={{ ...tentativeLabelStyle(), marginTop: 10 }}>
        Note
        <textarea
          value={note}
          onChange={(event) => onNote(event.target.value)}
          rows={3}
          placeholder="I can be flexible by an hour, prefer afternoons, etc."
          style={{ ...tentativeInputStyle(), resize: "vertical", lineHeight: 1.45 }}
        />
      </label>
      <div style={{ marginTop: 14 }}>
        <Button
          label="Send tentative request"
          variant="primary"
          onClick={onSubmit}
          loading={loading}
          full
        />
      </div>
    </div>
  );
}

function tentativeLabelStyle() {
  return {
    display: "grid",
    gap: 6,
    color: "var(--accent)",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1.1,
    textTransform: "uppercase",
  };
}

function tentativeInputStyle() {
  return {
    width: "100%",
    padding: "12px 13px",
    borderRadius: 12,
    border: "1.5px solid var(--border-strong)",
    background: "var(--bg-elevated)",
    color: "var(--text)",
    font: "inherit",
    fontSize: 14,
    letterSpacing: 0,
  };
}

/**
 * Surfaces the customer's monthly design-generation budget so they
 * understand each generation costs against their plan.
 *
 * Three states:
 *   - remaining > 1            → muted info ("4 of 6 left this month")
 *   - remaining === 1          → warning ("This is your last one")
 *   - remaining === 0          → blocking message + suggest re-using
 *                                an existing design or upgrading
 */
function QuotaBanner({ quota, plans, currencyCode, loadingTier, onSubscribe }) {
  if (!quota) return null;
  const { used, allowed, remaining, plan, resetAt } = quota;
  const isBlocked = remaining <= 0;
  const isWarn    = !isBlocked && remaining === 1;
  const reset     = resetAt
    ? new Date(resetAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  const tone = isBlocked
    ? { bg: "rgba(192, 32, 32, 0.10)", border: "1.5px solid rgba(192, 32, 32, 0.32)", icon: "🛑", color: "#A02020" }
    : isWarn
      ? { bg: "rgba(201, 168, 76, 0.14)", border: "1.5px solid rgba(201, 168, 76, 0.36)", icon: "⚠️", color: "#7A5800" }
      : { bg: "rgba(196, 135, 154, 0.10)", border: "1px solid var(--border)", icon: "✨", color: "var(--text-soft)" };

  return (
    <div
      role={isBlocked ? "alert" : undefined}
      style={{
        marginTop: 14, marginBottom: 14,
        padding: "12px 14px",
        background: tone.bg,
        border: tone.border,
        borderRadius: 14,
        display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap",
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>{tone.icon}</span>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: tone.color }}>
          {isBlocked ? (
            <>You've used all {allowed} design generations this month.</>
          ) : (
            <>{remaining} of {allowed} design generation{allowed === 1 ? "" : "s"} left ({plan} plan)</>
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3, lineHeight: 1.6 }}>
          {isBlocked ? (
            <>
              You can still book by <a href="/history" style={{ color: "var(--accent)", fontWeight: 600 }}>re-using
              a previous design</a> for free, or upgrade your plan{reset ? `. Quota resets ${reset}.` : "."}
            </>
          ) : (
            <>
              Each generation counts as one — covers AI text and photoreal renders.
              {used > 0 && allowed !== 999999 && reset
                ? ` ${used} used so far · resets ${reset}.`
                : ""}
            </>
          )}
        </div>
        {isBlocked && (
          <SubscriptionUpgradeOptions
            plans={plans}
            currencyCode={currencyCode}
            loadingTier={loadingTier}
            onSubscribe={onSubscribe}
            compact
          />
        )}
      </div>
    </div>
  );
}

function SubscriptionUpgradeOptions({ plans, currencyCode = "USD", loadingTier, onSubscribe, compact = false }) {
  const choices = [
    {
      tier: "silver",
      name: plans?.silver?.name || "Silver",
      price: formatPlanPrice(plans?.silver, currencyCode),
      perks: plans?.silver?.perks || ["More AI generations", "Priority slots", "Member booking perks"],
    },
    {
      tier: "gold",
      name: plans?.gold?.name || "Gold",
      price: formatPlanPrice(plans?.gold, currencyCode),
      perks: plans?.gold?.perks || ["Highest design allowance", "Member-only designs", "Premium booking perks"],
      highlight: true,
    },
  ];
  return (
    <div style={{
      width: "100%",
      marginTop: compact ? 12 : 16,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))",
      gap: 10,
    }}>
      {choices.map((plan) => (
        <div key={plan.tier} style={{
          borderRadius: 14,
          padding: compact ? 12 : 14,
          background: plan.highlight ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))" : "var(--bg-soft)",
          color: plan.highlight ? "#fff" : "var(--text)",
          border: "1px solid var(--border)",
        }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 800 }}>{plan.name}</div>
          <div style={{ fontSize: 13, opacity: 0.86, marginTop: 4 }}>{plan.price}</div>
          <ul style={{ fontSize: 12, marginTop: 8, paddingLeft: 16, opacity: 0.86, lineHeight: 1.55 }}>
            {plan.perks.slice(0, 3).map((perk) => <li key={perk}>{perk}</li>)}
          </ul>
          <Button
            label={loadingTier === plan.tier ? "Opening…" : `Choose ${plan.name}`}
            variant={plan.highlight ? "gold" : "primary"}
            size="sm"
            loading={loadingTier === plan.tier}
            disabled={!onSubscribe || Boolean(loadingTier)}
            onClick={() => onSubscribe?.(plan.tier)}
          />
        </div>
      ))}
    </div>
  );
}

function formatPlanPrice(plan, currencyCode = "USD") {
  if (!plan) return `${CURRENCY_SYMBOLS[currencyCode] || currencyCode}/quarter`;
  const code = String(plan.currency || currencyCode || "USD").toUpperCase();
  const amount = (Number(plan.priceCents || 0) / 100).toFixed(2);
  return `${CURRENCY_SYMBOLS[code] || code}${amount}/quarter`;
}

/**
 * Tiny banner that surfaces the skin-tone personalisation. The OpenAI
 * pipeline already includes `skin_tone` in the render prompt so the
 * generated hand matches the customer's tone — this just makes that fact
 * legible, and nudges users with no tone set to add one for a closer match.
 */
function SkinTonePreviewBanner({ skinTone }) {
  const tone = skinTone?.trim?.();
  if (tone) {
    return (
      <div style={{
        marginTop: 14, padding: "10px 14px",
        background: "rgba(232, 180, 184, 0.16)",
        border: "1px solid rgba(232, 180, 184, 0.32)",
        borderRadius: 12,
        display: "flex", alignItems: "center", gap: 10,
        fontSize: 12.5, color: "rgba(255,255,255,0.85)",
      }}>
        <span aria-hidden="true">✨</span>
        <span>
          Each preview is rendered on a hand matching your skin tone
          (<b style={{ color: "var(--gold-light)" }}>{tone}</b>).
        </span>
      </div>
    );
  }
  return (
    <div style={{
      marginTop: 14, padding: "10px 14px",
      background: "rgba(201, 168, 76, 0.14)",
      border: "1px solid rgba(201, 168, 76, 0.32)",
      borderRadius: 12,
      display: "flex", alignItems: "center", gap: 10,
      fontSize: 12.5, color: "rgba(255,255,255,0.85)",
    }}>
      <span aria-hidden="true">💡</span>
      <span style={{ flex: 1 }}>
        For a more accurate preview, add your skin tone in your profile so
        we render the design on a matching hand.
      </span>
      <a href="/profile" style={{
        color: "var(--gold-light)", fontWeight: 700, fontSize: 12.5,
        textDecoration: "none", whiteSpace: "nowrap",
      }}>Add tone →</a>
    </div>
  );
}
